import {
  PERMISSION_LABELS,
  PRODUCT_META_LIST,
  PermissionCode,
  PermissionNamespace,
  ProductStatus,
  RoleCode,
  employeePermissionsForNamespace,
  permissionNamespace,
  rolePermissionCeiling,
  type CreateRoleRequest,
  type ManagedRole,
  type PermissionCode as PermissionCodeType,
  type UpdateRoleRequest,
} from '@gb-ticket/shared-types';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useMe } from '@/platform/auth/use-me';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';

const NAMESPACE_LABELS: Record<PermissionNamespace, string> = {
  platform: 'Plattform',
  helpdesk: 'Helpdesk',
  intranet: 'Intranet',
  leave: 'Urlaub',
  fleet: 'Fuhrpark',
  maintenance: 'Wartungsprotokolle',
  onboarding: 'Eintritt und Austritt',
};

const LIVE_PRODUCTS = PRODUCT_META_LIST.filter(
  (meta) => meta.status === ProductStatus.LIVE && meta.access.kind === 'namespace',
);

function withProductAccess(
  current: readonly PermissionCodeType[],
  namespace: PermissionNamespace,
  enabled: boolean,
): PermissionCodeType[] {
  const rest = current.filter((permission) => permissionNamespace(permission) !== namespace);
  if (!enabled) {
    return rest;
  }
  if (rest.length !== current.length) {
    return [...current];
  }
  return [...rest, ...employeePermissionsForNamespace(namespace)];
}

const PERMISSION_GROUPS = Object.values(PermissionNamespace).map((namespace) => ({
  namespace,
  permissions: Object.values(PermissionCode).filter(
    (permission) => permissionNamespace(permission) === namespace,
  ),
}));

export type RoleFormMode = 'create' | 'edit' | 'copy';

type RoleFormDialogProps = {
  open: boolean;
  mode: RoleFormMode;
  source: ManagedRole | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: CreateRoleRequest | UpdateRoleRequest) => Promise<void>;
};

export function RoleFormDialog({
  open,
  mode,
  source,
  busy,
  onOpenChange,
  onSubmit,
}: RoleFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        {open ? (
          <RoleFormFields
            key={mode === 'edit' ? source?.id : mode === 'copy' ? `${source?.id}-copy` : 'new'}
            mode={mode}
            source={source}
            busy={busy}
            onOpenChange={onOpenChange}
            onSubmit={onSubmit}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function copyName(name: string): string {
  const suffix = ' (Kopie)';
  const base = name.trim();
  return `${base.slice(0, 80 - suffix.length).trimEnd()}${suffix}`;
}

function RoleFormFields({
  mode,
  source,
  busy,
  onOpenChange,
  onSubmit,
}: Omit<RoleFormDialogProps, 'open'>) {
  const editing = mode === 'edit';
  const [name, setName] = useState(
    mode === 'copy' && source ? copyName(source.name) : (source?.name ?? ''),
  );
  const [description, setDescription] = useState(source?.description ?? '');
  const [assignToNewUsers, setAssignToNewUsers] = useState(
    editing ? (source?.assignToNewUsers ?? false) : false,
  );
  const [permissions, setPermissions] = useState<PermissionCode[]>(source?.permissions ?? []);
  const keepsEmployeeBaseline = editing && source?.code === RoleCode.END_USER;
  const me = useMe();
  const actorPermissions = me.data?.permissions ?? [];
  const ceilingRole = editing ? (source?.code ?? null) : null;

  function withinCeiling(next: readonly PermissionCode[]): boolean {
    return (
      rolePermissionCeiling({
        actorPermissions,
        roleCode: ceilingRole,
        permissions: next,
      }) == null
    );
  }

  function setPermission(permission: PermissionCode, checked: boolean) {
    setPermissions((current) =>
      checked
        ? [...new Set([...current, permission])]
        : current.filter((item) => item !== permission),
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'edit'
            ? 'Berechtigungsgruppe bearbeiten'
            : mode === 'copy'
              ? 'Berechtigungsgruppe kopieren'
              : 'Berechtigungsgruppe anlegen'}
        </DialogTitle>
        <DialogDescription>
          {mode === 'copy'
            ? 'Rechte und Beschreibung stammen aus der Vorlage. Die neue Gruppe wird nicht automatisch neuen Benutzern zugeordnet.'
            : editing && source?.isSystem
              ? 'Der Code bleibt fest. Name, Beschreibung, Zuordnung und Rechte können geändert werden.'
              : 'Benutzer erhalten die ausgewählten Rechte automatisch über diese Gruppe.'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="role-name">Name</Label>
          <Input
            id="role-name"
            value={name}
            maxLength={80}
            autoComplete="off"
            onChange={(event) => setName(event.target.value)}
          />
          {editing && source ? (
            <p className="font-mono text-xs text-muted-foreground">{source.code}</p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="role-description">Beschreibung (optional)</Label>
          <Textarea
            id="role-description"
            value={description}
            maxLength={500}
            rows={2}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>

        <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-3">
          <div>
            <Label htmlFor="role-default">Neuen Benutzern zuordnen</Label>
            <p className="text-xs text-muted-foreground">
              {keepsEmployeeBaseline
                ? 'Endnutzer bleibt neuen Konten zugeordnet.'
                : 'Gilt für neue Konten aus Microsoft 365, LDAP, E-Mail und Webhooks.'}
            </p>
          </div>
          <Switch
            id="role-default"
            checked={keepsEmployeeBaseline || assignToNewUsers}
            disabled={keepsEmployeeBaseline}
            onCheckedChange={setAssignToNewUsers}
          />
        </div>

        <div className="space-y-3">
          <div>
            <h3 className="text-sm font-medium">Berechtigungen</h3>
            <p className="text-xs text-muted-foreground">
              Eine Gruppe darf leer bleiben, wenn sie nur als Channel-Autorengruppe dient.
            </p>
          </div>
          <div className="space-y-2">
            {LIVE_PRODUCTS.map((product) => {
              const namespace =
                product.access.kind === 'namespace' ? product.access.namespace : null;
              if (!namespace) {
                return null;
              }
              const switchId = `product-${product.code}`;
              const enabled = permissions.some(
                (permission) => permissionNamespace(permission) === namespace,
              );
              return (
                <div
                  key={product.code}
                  className="flex items-center justify-between gap-4 rounded-md border px-3 py-3"
                >
                  <Label htmlFor={switchId}>{product.labelDe}</Label>
                  <Switch
                    id={switchId}
                    checked={enabled}
                    onCheckedChange={(checked) =>
                      setPermissions((current) => {
                        const next = withProductAccess(current, namespace, checked);
                        return withinCeiling(next) ? next : current;
                      })
                    }
                  />
                </div>
              );
            })}
          </div>
          {PERMISSION_GROUPS.map((group) => (
            <fieldset key={group.namespace} className="rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">
                {NAMESPACE_LABELS[group.namespace]}
              </legend>
              <div className="grid gap-2 pt-1 sm:grid-cols-2">
                {group.permissions.map((permission) => (
                  <label
                    key={permission}
                    className="flex items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted/50"
                  >
                    <Checkbox
                      checked={permissions.includes(permission)}
                      disabled={
                        !permissions.includes(permission) &&
                        !withinCeiling([...permissions, permission])
                      }
                      onCheckedChange={(checked) => setPermission(permission, checked === true)}
                    />
                    <span>
                      <span className="block">{PERMISSION_LABELS[permission]}</span>
                      <span className="block font-mono text-[11px] text-muted-foreground">
                        {permission}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Abbrechen
        </Button>
        <Button
          type="button"
          disabled={busy || name.trim().length < 2}
          onClick={() =>
            void onSubmit({
              name: name.trim(),
              description: description.trim() || null,
              assignToNewUsers,
              permissions,
            })
          }
        >
          {editing ? 'Speichern' : 'Anlegen'}
        </Button>
      </DialogFooter>
    </>
  );
}
